import { Autocomplete, TextField } from '@mui/material';
import { useState, SyntheticEvent, useMemo, useRef } from 'react';
import { FilterActionName, useFilterAction, useFilterContext, useLocalSearchFilter, useSemanticFilter, useSystemPropertiesFilter, useTextFilter } from '../../state/FilterContext';
import { useSiteContext } from '../../state/SiteContext';
import { TextExtractor, findFirstComponentOfType } from '../internal/TextExtractor';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { applyDefaults, getStateAsLocationQuery } from '../../state/state-url-management';
import { useFilterStateController } from '../../state/filter-state-controller.context';
import { buildFilterRedirectNavigateTo, hasFilterRedirectNavigateIntent } from '../../shared/utils/filter-redirect-url.utils';
import { useToggleSwitchController } from '../../state/toggle-switch-controller.context';
import { activateToggleSwitchByName } from './toggle-switch-activation.utils';

interface StateStringInputProps {
  label?: string,
  redirect?: string,
  redirectLayout?: string,
  activateToggleSwitch?: string,
  autocompleteProps: string,
  textfieldProps: string,
  state?: "global-search" | "local-search" | "semantic-search" | "global-local-search",
  isSemantic?: boolean, // Only used for backwards compatibility
  children: React.ReactNode,
}

/***
 * A component that allows the user to enter a string value into a state.
 * The value may be added to the page URL as a query string parameter depending on the state.
 */
export function StateStringInput({ label, redirect, redirectLayout = "", activateToggleSwitch, autocompleteProps, textfieldProps, state, isSemantic, children }: StateStringInputProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const semanticFilter = useSemanticFilter();
  const textFilter = useTextFilter();
  const localSearchFilter = useLocalSearchFilter();
  const systemProperties = useSystemPropertiesFilter();
  const { siteState } = useSiteContext();
  const updateSystemProperties = useFilterAction(FilterActionName.UPDATE_SYSTEM_PROPERTIES);
  const { filterState } = useFilterContext();
  const updateTextFilter = useFilterAction(FilterActionName.UPDATE_TEXT);
  const updateSemanticFilter = useFilterAction(FilterActionName.UPDATE_SEMANTIC);
  const updateLocalSearchFilter = useFilterAction(FilterActionName.UPDATE_LOCAL_SEARCH);
  const controller = useFilterStateController();
  const redirectWarningLoggedRef = useRef(false);
  const toggleSwitchController = useToggleSwitchController();

  const autocompletePropsObject = JSON.parse(autocompleteProps || "{}");
  const textfieldPropsObject = JSON.parse(textfieldProps || "{}");

  // Get the label from the label child
  const labelChild = useMemo(() => findFirstComponentOfType(children, "arg-label"), [children]);
  const [labelValue, setLabelValue] = useState(label || "");

  const usedState = useMemo(() => {
    if (isSemantic) {
      console.warn("StateStringInput: The isSemantic prop is deprecated. Use state instead.");
      return "semantic-search";
    }
    else if (state) {
      return state;
    }
    else {
      console.warn("StateStringInput: Omitting state argument is deprecated and will be required in a future version. For backwards compatibility, the default state is global-search.");
      return "global-search";
    }
  }, [isSemantic, state]);

  const hasRedirectNavigateIntent = hasFilterRedirectNavigateIntent(redirect, redirectLayout);

  const handleEnterKeyRedirect = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key !== "Enter" || !hasRedirectNavigateIntent || controller) {
      return;
    }

    const inputElement = event.target as HTMLInputElement | null;
    const inputValue = inputElement?.value ?? "";
    if (!inputValue.trim()) {
      return;
    }

    setFilterValue(inputValue);
  };

  const setFilterValue = (value: string | null) => {
    if (controller) {
      if (hasRedirectNavigateIntent && !redirectWarningLoggedRef.current) {
        console.warn('[FilterStateController] Ignoring redirect and redirectLayout behavior for "StateStringInput" in controller scope.');
        redirectWarningLoggedRef.current = true;
      }

      controller.requestChange((stateSnapshot) => {
        const nextState = { ...stateSnapshot };
        if (usedState === "global-search" || usedState === "semantic-search") {
          const nextSystemProps = { ...(nextState.systemProperties || {}) };
          delete nextSystemProps["hid"];
          nextState.systemProperties = nextSystemProps;
        }

        switch (usedState) {
          case "semantic-search":
            nextState.semantic = value || undefined;
            break;
          case "local-search":
            nextState.localSearch = value || undefined;
            break;
          case "global-local-search":
            nextState.localSearch = value || undefined;
            nextState.text = value || undefined;
            break;
          default:
            nextState.text = value || undefined;
            break;
        }
        return nextState;
      }, { source: `statestringinput:${usedState}` });
      activateToggleSwitchByName(activateToggleSwitch, toggleSwitchController, "StateStringInput");
      return;
    }

    if (hasRedirectNavigateIntent) {
      const filterStateCopy = { ...filterState };
      if (usedState === "global-search" || usedState === "semantic-search") {
        delete filterStateCopy.systemProperties?.["hid"];
      }

      switch (usedState) {
        case "semantic-search":
          filterStateCopy.semantic = value || undefined;
          break;
        case "local-search":
          filterStateCopy.localSearch = value || undefined;
          break;
        case "global-local-search":
          filterStateCopy.localSearch = value || undefined;
          filterStateCopy.text = value || undefined;
          break;
        default:
          filterStateCopy.text = value || undefined;
          break;
      }

      if (siteState?.site) {
        applyDefaults(filterStateCopy, siteState?.site);
      }
      const searchParams = getStateAsLocationQuery(filterStateCopy);
      const navigateTo = buildFilterRedirectNavigateTo({
        effectiveSitePath: siteState.sitePath ?? "",
        redirect,
        redirectLayout,
        currentPathname: location.pathname,
        searchParamsString: searchParams.toString(),
        locationHash: location.hash,
        activeToggles: activateToggleSwitch,
      });
      if (navigateTo) {
        switch (usedState) {
          case "semantic-search":
            updateSemanticFilter(value);
            break;
          case "local-search":
            updateLocalSearchFilter(value);
            break;
          case "global-local-search":
            updateLocalSearchFilter(value);
            updateTextFilter(value);
            break;
          default:
            updateTextFilter(value);
            break;
        }
        if (usedState === "global-search" || usedState === "semantic-search") {
          const systemProps = { ...systemProperties };
          delete systemProps["hid"];
          updateSystemProperties(systemProps);
        }
        activateToggleSwitchByName(activateToggleSwitch, toggleSwitchController, "StateStringInput");
        navigate(navigateTo, { replace: true });
      }
    }
    else {
      switch (usedState) {
        case "semantic-search":
          updateSemanticFilter(value);
          break;
        case "local-search":
          updateLocalSearchFilter(value);
          break;
        case "global-local-search":
          updateLocalSearchFilter(value);
          updateTextFilter(value);
          break;
        default:
          updateTextFilter(value);
          break;
      }
      // If the user does a global or semanticsearch, remove the help id filter so they can search in the whole site
      // Only do this when redirect is NOT set, because when redirect is set, the change is included in the URL
      if (usedState === "global-search" || usedState === "semantic-search") {
        const systemProps = { ...systemProperties };
        delete systemProps["hid"];
        updateSystemProperties(systemProps);
      }
      activateToggleSwitchByName(activateToggleSwitch, toggleSwitchController, "StateStringInput");
    }
  }

  textfieldPropsObject.label = labelValue || textfieldPropsObject.label || "Search";


  const value = useMemo(() => {
    if (usedState === "semantic-search") {
      return semanticFilter;
    }
    else if (usedState === "local-search") {
      return localSearchFilter;
    }
    else {
      return textFilter;
    }
  }, [usedState, semanticFilter, localSearchFilter, textFilter]);

  return <>
    {labelChild && (
      <TextExtractor onExtractedText={setLabelValue}>
        {labelChild?.props.children}
      </TextExtractor>
    )}
    <Autocomplete freeSolo {...autocompletePropsObject}
      id="textfilter-autocomplete"
      options={[]}
      value={value}
      onKeyDown={(event: React.KeyboardEvent<HTMLElement>) => {
        handleEnterKeyRedirect(event);
      }}
      onChange={(event: SyntheticEvent<Element, Event>, newVal: string | null) => {
        setFilterValue(newVal);
      }}
      onBlur={(event: SyntheticEvent<Element, Event>) => {
        const target = event.target as HTMLInputElement;
        if (target.value !== value) {
          setFilterValue(target.value);
        }
      }}
      renderInput={(params: any) => {
        return <TextField {...params} {...textfieldPropsObject} />
      }}
    />
  </>;
}